import React, { useCallback, useMemo } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { Box, type SxProps, type Theme, Typography, useTheme } from '@mui/material';
import { AxiosError } from 'axios';
import { mergeSx } from 'merge-sx';
import type { MuiTelInputCountry } from 'mui-tel-input';

import {
  FolderType,
  OrganizationDTOTalentUniqueFieldsItem,
  TalentCreateDTO,
  TalentDTO,
  TalentDTOType,
  UpdateTalentRequest,
  useGetOrganizationBySlug,
  useGetTalentById,
  useGetTalentByIdQueryOptions,
  useUpdateTalent
} from '@/api/generated';
import BackdropLoader from '@/components/BackdropLoader';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import ChipSelect from '@/components/fields/ChipSelect';
import Field from '@/components/fields/Field';
import LocationTreeSelect from '@/components/fields/Location/LocationTreeSelect';
import TalentLocationTreeSelect from '@/components/fields/Location/TalentLocationTreeSelect';
import PhoneInput from '@/components/fields/PhoneInput';
import TextInput from '@/components/fields/TextInput';
import { CommonFormFooter } from '@/components/Form/Form';
import { useTalentPermissions } from '@/hooks/permissions/useTalentPermissions';
import useConfirmation from '@/hooks/useConfirmation';
import { OrganizationStructureProvider } from '@/hooks/useOrganizationStructure';
import { ReadonlyProvider } from '@/hooks/useReadonly';
import { useSnackbar } from '@/hooks/useSnackbar';
import useTalentBasicInfoForm from '@/hooks/useTalentBasicInfoForm';
import countries from '@/utils/countries';
import OrganizationDropdownSelect from '@/views/fields/OrganizationDropdownSelect/OrganizationDropdownSelect';

import { TalentBasicInfoViewType } from './constants';

const ALLOWED_LOCATION_TYPES: FolderType[] = [FolderType.FOLDER];

const BasicInfoForm = ({ talent, talentId }: { talent: TalentDTO; talentId: string }) => {
  const { t } = useTranslation(['talents', 'validation', 'common', 'settings']);
  const { spacing } = useTheme();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  const defaultValues = useMemo(
    () => ({
      firstName: talent.firstName,
      lastName: talent.lastName,
      idNumber: talent.idNumber,
      passportNumber: talent.passportNumber,
      phone: talent.phone,
      email: talent.email,
      type: talent.type,
      organizationSlug: talent.organization?.slug,
      locations: talent.locations?.map(({ slug }) => slug!)
    }),
    [talent]
  );

  const { formMethods } = useTalentBasicInfoForm({
    defaultValues,
    viewType: TalentBasicInfoViewType.EDIT
  });
  const { reset: resetForm, formState } = formMethods;
  const { proceed, reset: resetBlocker, isBlocked } = useConfirmation(formState);
  const { mutateAsync: updateTalent } = useUpdateTalent<AxiosError>();
  const { queryKey } = useGetTalentByIdQueryOptions(talentId);

  const { value: { canUpdate } = {} } = useTalentPermissions({
    variables: { talentId }
  });

  const handleSubmitFailed = useCallback(() => {
    showMessage(t('validation:alerts.failed'));
    resetBlocker?.();
  }, [resetBlocker, showMessage, t]);

  const handleDiscardChanges = useCallback(() => {
    resetForm?.();
    proceed?.();
  }, [proceed, resetForm]);

  const handleSubmit = useCallback(
    async (data: Omit<UpdateTalentRequest, 'locations'> & { locations?: string[] }) => {
      if (!canUpdate) return;

      await updateTalent(
        {
          talentId,
          data: {
            firstName: data.firstName,
            lastName: data.lastName,
            phone: data.phone,
            passportNumber: data.passportNumber,
            idNumber: data.idNumber,
            email: data.email,
            type: data.type
          }
        },
        {
          onSuccess: async () => {
            await queryClient.invalidateQueries(queryKey);
            resetForm(data, { keepIsSubmitSuccessful: true });
            if (isBlocked) proceed!();
          },
          onError: error => {
            if (error.response?.status === 400) return showMessage(t('settings:settings.alerts.invalid'));

            return showMessage(t('common:error.somethingWentWrong'));
          }
        }
      );
    },
    [canUpdate, isBlocked, proceed, queryClient, queryKey, resetForm, showMessage, t, talentId, updateTalent]
  );

  return (
    <FormProvider {...formMethods}>
      <Box
        component="form"
        onSubmit={formMethods.handleSubmit(handleSubmit)}
        sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Typography component="h2" variant="body1Bold" sx={{ padding: 3 }}>
          {t('talents:layouts.talentEditModal.tabs.basicInfo')}
        </Typography>
        <TalentBasicInfoFields
          sx={{ marginBottom: spacing(2), paddingX: spacing(3) }}
          talentId={talentId}
          isReadonly={!canUpdate}
        />
        <CommonFormFooter sx={{ marginTop: 'auto' }} disableSubmit={!canUpdate} />
      </Box>

      <ConfirmationModal
        open={isBlocked}
        onConfirm={formMethods.handleSubmit(handleSubmit, handleSubmitFailed)}
        onClose={resetBlocker!}
        onDiscard={handleDiscardChanges}
        discardButtonLabel={t('settings:settings.discardButton')}
        title={t('settings:settings.unsavedChangesTitle')}
      />
    </FormProvider>
  );
};

BasicInfoForm.displayName = 'BasicInfoForm';

const BasicInfo = () => {
  const { talentId } = useParams<{ talentId: string }>();
  const { data: talent, isLoading: isTalentLoading } = useGetTalentById<TalentDTO>(talentId!, {
    query: {
      enabled: !!talentId
    }
  });

  const organizationSlug = talent?.organization?.slug;
  const { isLoading: isOrgLoading } = useGetOrganizationBySlug(
    organizationSlug!,
    { isFull: true },
    { query: { enabled: !!organizationSlug } }
  );

  const showLoader = isTalentLoading || (!!organizationSlug && isOrgLoading);

  if (!talentId) return null;
  if (showLoader) return <BackdropLoader />;
  if (!talent) return null;

  return <BasicInfoForm talent={talent} talentId={talentId} />;
};

BasicInfo.displayName = 'BasicInfo';

export default BasicInfo;

export const TalentBasicInfoFields = ({
  viewType = TalentBasicInfoViewType.EDIT,
  sx,
  disabledFields = {},
  talentId,
  isReadonly
}: {
  viewType?: TalentBasicInfoViewType;
  sx?: SxProps<Theme>;
  disabledFields?: {
    type?: boolean;
    locations?: boolean;
  };
  talentId?: string;
  isReadonly?: boolean;
}) => {
  const { t } = useTranslation(['talents', 'settings']);
  const { spacing } = useTheme();
  const { formState, watch } = useFormContext<TalentCreateDTO>();
  const organizationSlug = watch('organizationSlug');
  const { data: organization } = useGetOrganizationBySlug(
    organizationSlug!,
    { isFull: true },
    { query: { enabled: !!organizationSlug } }
  );

  const { idNumberIsIdentity, passportIsIdentity, phoneIsIdentity, defaultPhoneCountry } = useMemo(() => {
    const firstCountry = organization?.countries?.[0];
    const twoLetterCode = firstCountry ? countries.alpha3ToAlpha2(firstCountry) : undefined;

    return {
      idNumberIsIdentity: organization?.talentUniqueFields?.includes(OrganizationDTOTalentUniqueFieldsItem.ID_NUMBER),
      passportIsIdentity: organization?.talentUniqueFields?.includes(
        OrganizationDTOTalentUniqueFieldsItem.PASSPORT_NUMBER
      ),
      phoneIsIdentity: organization?.talentUniqueFields?.includes(OrganizationDTOTalentUniqueFieldsItem.PHONE),
      emailIsIdentity: organization?.talentUniqueFields?.includes(OrganizationDTOTalentUniqueFieldsItem.EMAIL),
      defaultPhoneCountry: twoLetterCode as MuiTelInputCountry | undefined
    };
  }, [organization?.talentUniqueFields, organization?.countries]);

  return (
    <OrganizationStructureProvider organizationSlug={organizationSlug}>
      <ReadonlyProvider readonly={isReadonly || formState.isSubmitting}>
        <Box
          sx={mergeSx(
            {
              display: 'grid',
              gridTemplateColumns: `repeat(2, minmax(auto, ${spacing(40)}))`,
              gap: spacing(2, 1.5)
            },
            sx
          )}
        >
          <Field
            withFormControl
            component={TextInput}
            name="firstName"
            innerProps={{ markAsRequired: true }}
            label={t('talents:layouts.talentEditModal.fields.firstName')}
            outerSx={{ p: 0, width: '100%' }}
          />
          <Field
            withFormControl
            component={TextInput}
            innerProps={{ markAsRequired: true }}
            name="lastName"
            label={t('talents:layouts.talentEditModal.fields.lastName')}
            outerSx={{ p: 0, width: '100%' }}
          />
          <Field
            withFormControl
            component={TextInput}
            innerProps={{
              markAsRequired: idNumberIsIdentity
            }}
            name="idNumber"
            label={t('talents:layouts.talentEditModal.fields.idNumber')}
            outerSx={{ p: 0, width: '100%' }}
          />
          <Field
            withFormControl
            component={PhoneInput}
            innerProps={{ markAsRequired: phoneIsIdentity, defaultCountry: defaultPhoneCountry }}
            name="phone"
            label={t('talents:layouts.talentEditModal.fields.phone')}
            outerSx={{ p: 0, width: '100%' }}
          />
          {passportIsIdentity && (
            <Field
              withFormControl
              component={TextInput}
              innerProps={{
                markAsRequired: true
              }}
              name="passportNumber"
              label={t('talents:layouts.talentEditModal.fields.passportNumber')}
              outerSx={{ p: 0, width: '100%' }}
            />
          )}
          <Field
            withFormControl
            component={TextInput}
            innerProps={{ markAsRequired: true }}
            name="email"
            label={t('talents:layouts.talentEditModal.fields.email')}
            outerSx={{ p: 0, width: '100%' }}
          />
          <Field
            withFormControl
            component={ChipSelect}
            name="type"
            label={t('talents:layouts.talentEditModal.fields.type')}
            outerSx={{ p: 0, gridRowStart: 4 }}
            innerProps={{
              disabled: disabledFields.type || isReadonly || formState.isSubmitting,
              enforcedValue: true,
              color: 'primary',
              options: [
                {
                  label: t(`filters:filters.options.talentTypes.candidate`),
                  value: TalentDTOType.CANDIDATE
                },
                { label: t(`filters:filters.options.talentTypes.employee`), value: TalentDTOType.EMPLOYEE }
              ],
              sx: mergeSx({ alignSelf: 'flex-start' }, disabledFields.type ? { filter: 'grayscale(100%)' } : {})
            }}
          />
          <Field
            withFormControl
            component={OrganizationDropdownSelect}
            name="organizationSlug"
            label={t('talents:layouts.talentEditModal.fields.location')}
            outerSx={{ p: 0, gridColumn: 'span 2', '& .MuiFormHelperText-root': { display: 'none' } }}
            innerProps={{
              isDisabled:
                disabledFields.locations ||
                viewType === TalentBasicInfoViewType.EDIT ||
                isReadonly ||
                formState.isSubmitting,
              markAsRequired: viewType === TalentBasicInfoViewType.CREATE,
              usePortal: true,
              isMulti: false,
              sx: { alignSelf: 'flex-start', width: '100%' }
            }}
          />
          {viewType === TalentBasicInfoViewType.CREATE && (
            <Field
              withFormControl
              component={LocationTreeSelect}
              name="locations"
              label=""
              outerSx={{ p: 0, gridColumn: 'span 2' }}
              innerProps={{
                disabled: disabledFields.locations || isReadonly || formState.isSubmitting,
                organizationSlug,
                isMulti: true,
                sx: { alignSelf: 'flex-start', width: '100%' },
                allowedTypes: ALLOWED_LOCATION_TYPES
              }}
            />
          )}
          {viewType === TalentBasicInfoViewType.EDIT && (
            <TalentLocationTreeSelect
              talentId={talentId!}
              organizationSlug={organizationSlug!}
              allowedTypes={ALLOWED_LOCATION_TYPES}
              disabled={isReadonly || formState.isSubmitting}
            />
          )}
        </Box>
      </ReadonlyProvider>
    </OrganizationStructureProvider>
  );
};
